import { computed, Injectable, signal } from '@angular/core'

import {
  Order,
  OrderAdditionalPackage,
  OrderCashOnDelivery,
  OrderDetailData,
  OrderPackage,
  OrderShipmentData,
  Shipment,
  ShipmentTracking,
} from '../models/order.entity'
import { OrdersRepository } from '../orders.repository'
import { HttpErrorResponse } from '@angular/common/http'
import { Printers } from '../../print-node'
import { OrdersService } from '../orders.service'
import { Carrier, CarriersService } from '../../carriers'
import { OrderField } from '../models/order-search.model'
import { ShippingAddress } from '../../../models/address.model'
import { Product, ProductSelection, ProductsService } from '../../products'
import { Bordereau } from '../../bordereaus'
import { PickingList } from '../../picking-lists'
import { Payment, PaymentsService } from '../../payments'
import { User } from '../../users'
import { Tenant } from '../../tenants'
import { DeepPartial } from '../../../models/util.model'
import {
  addOrderProducts,
  initOrder,
  setOrderCarrier,
  setOrderDefaults,
  unsetOrderCarrier,
  updateOrderAmounts,
} from '../libs/order.lib'
import { checkOrderCodPayment } from '../libs/order-payment.lib'
import {
  OrderChangeStatusAction,
  OrderDetailExtDataTuple,
} from '../models/order.types'
import { InvoicesService } from '../../invoices'
import { uniq, uniqBy } from 'lodash'
import { TranslationService } from '@evologi/shared/util-translation'
import { CustomersService } from '../../customers'
import {
  setOrderBillingAddressByCustomer,
  setOrderShippingAddressByCustomer,
} from '../libs/order-address.lib'
import { GoodsReturn, GoodsReturnsService } from '../../goods-returns'

interface OrderDetailState {
  order?: Order
  carrier?: Carrier
  error?: HttpErrorResponse
  fields: OrderField[]
  printers?: Printers
  products: Product[]
  packages: Product[]
  bordereau?: Bordereau
  pickingList?: PickingList
  goodsReturn?: GoodsReturn
  payment?: Payment
  shipment?: Shipment
  tracking?: ShipmentTracking
  tenantId?: string
  amountsWithTaxes: boolean
  amountsEditable: boolean
  discountPercentage: boolean
  dropshippingEnabled: boolean
  uploadUrl?: string
  isLoading: boolean
  isModified: boolean
}

const ORDER_DETAIL_INITIAL_STATE: OrderDetailState = {
  isLoading: false,
  isModified: false,
  products: [],
  packages: [],
  amountsWithTaxes: false,
  amountsEditable: false,
  discountPercentage: false,
  dropshippingEnabled: false,
  fields: ['carrierId', 'assignedWarehouseId'],
}

@Injectable()
export class OrderDetailStore {
  constructor(
    private ordersRepository: OrdersRepository,
    private ordersService: OrdersService,
    private paymentsService: PaymentsService,
    private carriersService: CarriersService,
    private invoicesService: InvoicesService,
    private customersService: CustomersService,
    private goodsReturnsService: GoodsReturnsService,
    private productsService: ProductsService,
    private translationService: TranslationService,
  ) {}

  private state = signal<OrderDetailState>(ORDER_DETAIL_INITIAL_STATE)

  // Selectors
  readonly order = computed(() => this.state().order)
  readonly carrier = computed(() => this.state().carrier)
  readonly printers = computed(() => this.state().printers)
  readonly products = computed(() => this.state().products)
  readonly packages = computed(() => this.state().packages)
  readonly bordereau = computed(() => this.state().bordereau)
  readonly pickingList = computed(() => this.state().pickingList)
  readonly payment = computed(() => this.state().payment)
  readonly shipment = computed(() => this.state().shipment)
  readonly goodsReturn = computed(() => this.state().goodsReturn)
  readonly tracking = computed(() => this.state().tracking)
  readonly amountsWithTaxes = computed(() => this.state().amountsWithTaxes)
  readonly amountsEditable = computed(() => this.state().amountsEditable)
  readonly discountPercentage = computed(() => this.state().discountPercentage)
  readonly dropshippingEnabled = computed(
    () => this.state().dropshippingEnabled,
  )
  readonly uploadUrl = computed(() => this.state().uploadUrl)
  readonly tenantId = computed(() => this.state().tenantId)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly isModified = computed(() => this.state().isModified)
  readonly isErrored = computed(() => !!this.state().error)
  // Effects

  async load(orderId?: string): Promise<void> {
    if (!orderId) {
      return
    }
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.ordersService.read$(orderId).subscribe({
        next: async (data) => {
          if (!data) {
            this.ordersRepository.notify({ code: 'ORDER_NOT_FOUND' })
            reject()
            return
          }
          const extData = await this.loadExtData(data)
          resolve()
          this.setData(
            data,
            this.state().order?._id
              ? this.state().order?._id !== data._id
              : false,
            extData,
          )
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  async reloadOrder(): Promise<void> {
    const order = this.order()
    if (!order?._id) {
      return
    }
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.ordersService.read$(order._id).subscribe({
        next: async (data) => {
          if (!data) {
            this.ordersRepository.notify({ code: 'ORDER_NOT_FOUND' })
            reject()
            return
          }
          resolve()
          this.setOrder(data, false)
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  async loadExtData(data: Order) {
    return await Promise.all([
      this.loadBordereau(data),
      this.loadCarrier(data.carrierId),
      this.checkPayment(data),
      this.loadShipmentData(data),
      this.loadPickingList(data),
      this.loadProducts(data),
      this.loadGoodsReturn(data),
      this.loadPackages(data),
    ])
  }

  async save(order: Order, notify?: boolean): Promise<boolean> {
    this.unsetError()

    return new Promise((resolve) => {
      const fields = this.state().fields
      this.ordersRepository.saveOrder$(order, fields).subscribe({
        next: (data) => {
          if (!data.order) {
            resolve(false)
            this.ordersRepository.notify({ code: 'ORDER_NOT_SAVED' })
            return
          }
          resolve(true)
          this.setData(data.order, false, this.detailDataToTuple(data.extData))
          if (notify === true) {
            this.ordersRepository.notify({ code: 'ORDER_SAVED' })
          }
        },
        error: (error) => {
          resolve(false)
          this.setError(error)
        },
      })
    })
  }

  async checkPayment(order: Order): Promise<OrderCashOnDelivery | undefined> {
    const paymentType = order?.header.paymentType
    if (!paymentType) {
      return
    }

    return new Promise((resolve) => {
      this.paymentsService.read$(paymentType).subscribe({
        next: (value) => {
          if (value) {
            resolve(checkOrderCodPayment(order, value))
          }
          resolve(undefined)
        },
        error: (error) => {
          resolve(undefined)
          this.setError(error)
        },
      })
    })
  }

  async loadPickingList(order: Order): Promise<PickingList | undefined> {
    const pickingListId =
      order?.pickingListId ??
      order?.statusHistory.find((s) => s.pickingListId !== undefined)
        ?.pickingListId

    if (!pickingListId) {
      return
    }

    return new Promise((resolve) => {
      this.ordersRepository.loadOrderPickingList$(pickingListId).subscribe({
        next: (value) => {
          resolve(value)
        },
        error: (error) => {
          resolve(undefined)
          this.setError(error)
        },
      })
    })
  }

  async loadGoodsReturn(order: Order): Promise<GoodsReturn | undefined> {
    const returned = order?.returned
    if (!returned) {
      return
    }

    if (
      order.header.rifOrder === undefined &&
      order.header.orderNumber === undefined
    ) {
      return
    }

    return new Promise((resolve) => {
      this.goodsReturnsService
        .search$(
          order.header.rifOrder
            ? { 'referenceOrder.refOrder': order.header.rifOrder, limit: 1 }
            : { 'referenceOrder.number': order.header.orderNumber, limit: 1 },
        )
        .subscribe({
          next: (value) => {
            resolve(value.data.length > 0 ? value.data[0] : undefined)
          },
          error: (error) => {
            resolve(undefined)
            this.setError(error)
          },
        })
    })
  }

  async loadPackages(order: Order): Promise<Product[] | undefined> {
    if (
      (!order.packages || order.packages.length < 1) &&
      (!order.additionalPackages || order.additionalPackages.length < 1)
    ) {
      return undefined
    }
    const productIds = (order.packages ?? [])
      .filter((p) => p.packageId !== undefined)
      .map((p) => p.packageId!)
    const additionalProductIds = (order.additionalPackages ?? [])
      .filter((p) => p.packageId !== undefined)
      .map((p) => p.packageId!)
    const unifProdIds = uniq([...productIds, ...additionalProductIds])
    if (unifProdIds.length < 1) {
      return undefined
    }

    return new Promise((resolve) => {
      this.productsService
        .search$({ _id: unifProdIds, productType: 'PACKAGE' })
        .subscribe({
          next: (value) => {
            resolve(value.data)
          },
          error: (error) => {
            resolve(undefined)
            this.setError(error)
          },
        })
    })
  }

  async loadBordereau(order: Order): Promise<Bordereau | undefined> {
    const bordereauId = order?.bordereauId
    if (!bordereauId) {
      return
    }

    return new Promise((resolve) => {
      this.ordersRepository.loadOrderBordereau$(order).subscribe({
        next: (value) => {
          resolve(value)
        },
        error: (error) => {
          resolve(undefined)
          this.setError(error)
        },
      })
    })
  }

  async loadShipmentData(order: Order): Promise<OrderShipmentData | undefined> {
    return new Promise((resolve) => {
      this.ordersRepository.getOrderShipmentData$(order).subscribe({
        next: (value) => {
          resolve(value)
        },
        error: (error) => {
          resolve(undefined)
          this.setError(error)
        },
      })
    })
  }

  async loadProducts(order: Order): Promise<Product[] | undefined> {
    return new Promise((resolve) => {
      this.ordersRepository.loadOrderProducts$(order).subscribe({
        next: (value) => {
          resolve(value)
        },
        error: (error) => {
          resolve(undefined)
          this.setError(error)
        },
      })
    })
  }

  async loadCarrier(
    carrierId: string | undefined,
  ): Promise<Carrier | undefined> {
    if (!carrierId) {
      return
    }

    return new Promise((resolve) => {
      this.carriersService.read$(carrierId).subscribe({
        next: (value) => {
          resolve(value)
        },
        error: (error) => {
          resolve(undefined)
          this.setError(error)
        },
      })
    })
  }

  async printLabel(): Promise<boolean> {
    this.unsetError()

    const order = this.order()
    const printers = this.printers()
    if (!order) {
      this.ordersRepository.notify({ code: 'ORDER_NOT_FOUND' })
      return false
    }
    if (!printers) {
      this.ordersRepository.notify({ code: 'PRINTERS_NOT_FOUND' })
      return false
    }

    const isModified = this.isModified()
    if (isModified) {
      const saved = await this.save(order)
      if (!saved) {
        return false
      }
    }

    return new Promise((resolve) => {
      this.ordersService.printLabel$(order._id, printers).subscribe({
        next: () => {
          resolve(true)
          this.ordersRepository.notify({ code: 'ORDER_PRINTED' })
        },
        error: (error) => {
          resolve(false)
          this.setError(error)
        },
      })
    })
  }

  async cancelShipment(): Promise<boolean> {
    const order = this.order()
    if (!order) {
      this.ordersRepository.notify({ code: 'ORDER_NOT_FOUND' })
      return false
    }

    this.unsetError()

    return new Promise<boolean>((resolve) => {
      this.ordersRepository.alert('SHIPMENT_CANCEL').then(async (response) => {
        if (response === 'success') {
          this.setIsLoading(true)
          const changed = await this.changeOrderStatus(order, 'cancelShipment')
          this.ordersRepository.notify({
            code: changed === true ? 'SHIPMENT_CANCELED' : 'UPDATE_ERRORED',
          })
          resolve(changed)
        } else {
          this.setIsLoading(false)
        }
      })
    })
  }

  async requestShipment(): Promise<boolean> {
    this.unsetError()
    const order = this.order()
    if (!order) {
      this.ordersRepository.notify({ code: 'ORDER_NOT_FOUND' })
      return false
    }

    return new Promise((resolve) => {
      this.ordersService.requestShipment$(order._id).subscribe({
        next: () => {
          resolve(true)
          this.ordersRepository.notify({ code: 'SHIPMENT_REQUESTED' })
          this.load(order._id)
        },
        error: (error) => {
          resolve(false)
          this.setError(error)
        },
      })
    })
  }

  async changeOrderStatus(
    order: Order,
    action: OrderChangeStatusAction,
  ): Promise<boolean> {
    const saved = await this.save(order)
    if (!saved) {
      return false
    }

    const updatedOrder = this.order()
    if (!updatedOrder) {
      this.ordersRepository.notify({ code: 'ORDER_NOT_FOUND' })
      return false
    }

    this.unsetError()
    return new Promise((resolve) => {
      this.ordersService.changeStatus$(updatedOrder._id, action).subscribe({
        next: async (data) => {
          const extData = await this.loadExtData(data)
          this.setData(data, false, extData)
          this.ordersRepository.notify({ code: 'ORDER_SAVED' })
          resolve(true)
        },
        error: (error) => {
          resolve(false)
          this.setError(error)
        },
      })
    })
  }

  async checkTransport() {
    const order = this.order()
    if (!order) {
      return
    }

    const carrier = await this.loadCarrier(order.carrierId)
    if (carrier) {
      this.setOrder(setOrderCarrier(order, carrier))
    } else {
      this.setOrder(unsetOrderCarrier(order))
    }
  }

  async cancel(): Promise<boolean> {
    const order = this.order()
    if (!order) {
      this.ordersRepository.notify({ code: 'ORDER_NOT_FOUND' })
      return false
    }

    const saved = await this.save(order)
    if (!saved) {
      return false
    }

    const checkInvoices = await this.checkInvoices()
    if (!checkInvoices) {
      return false
    }

    this.unsetError()

    return new Promise<boolean>((resolve) => {
      this.ordersRepository.alert('ORDER_CANCEL', order).then((response) => {
        if (response === 'success') {
          this.setIsLoading(true)
          this.ordersService.cancel$(order._id).subscribe({
            next: (value) => {
              resolve(true)
              this.setOrder(value, false)
              this.ordersRepository.notify({
                code: 'ORDER_CANCELED',
              })
            },
            error: (error: HttpErrorResponse) => {
              resolve(false)
              this.setError(error)
            },
          })
        } else {
          this.setIsLoading(false)
        }
      })
    })
  }

  // DMO Hack
  async checkInvoices(): Promise<boolean> {
    const order = this.order()
    if (!order) {
      this.ordersRepository.notify({ code: 'ORDER_NOT_FOUND' })
      return true
    }

    if (
      this.tenantId() !== '5c532bfb3f1a1334c000c7ce' && // DMO Pet Care Prod & Staging
      this.tenantId() !== '643571f39627c61a9c6da846' // DMO Spa Prod
    ) {
      return true
    }

    return new Promise((resolve) => {
      this.invoicesService.search$({ orderIds: [order._id] }).subscribe({
        next: async (data) => {
          if (data.data.length === 0) {
            resolve(true)
          }
          const deleted = await this.deleteInvoices(data.data[0]._id)
          resolve(deleted)
        },
        error: (error) => {
          resolve(false)
          this.setError(error)
        },
      })
    })
  }

  async deleteInvoices(invoiceId: string): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.ordersRepository
        .alert('INVOICES_DELETION')
        .then(async (response) => {
          if (response === 'success') {
            this.setIsLoading(true)
            const deleted = await this.deleteInvoice(invoiceId)
            if (deleted) {
              this.ordersRepository.notify({ code: 'INVOICE_DELETED' })
              resolve(true)
            } else {
              resolve(false)
            }
          } else {
            this.setIsLoading(false)
          }
        })
    })
  }

  async delete(): Promise<boolean> {
    const order = this.order()
    if (order?.status !== 'DRAFTED') {
      this.ordersRepository.notify({ code: 'WRONG_STATUS_DELETE' })
      return false
    }

    if (!order) {
      this.ordersRepository.notify({ code: 'ORDER_NOT_FOUND' })
      return false
    }

    return new Promise<boolean>((resolve) => {
      this.ordersRepository.alert('ORDER_DELETION', order).then((response) => {
        if (response === 'success') {
          this.setIsLoading(true)
          this.ordersService.delete$(order._id).subscribe({
            next: () => {
              resolve(true)
              this.ordersRepository.notify({
                code: 'ORDER_DELETED',
              })
            },
            error: (error: HttpErrorResponse) => {
              resolve(false)
              this.setError(error)
            },
          })
        } else {
          this.setIsLoading(false)
        }
      })
    })
  }

  async deleteInvoice(invoiceId: string): Promise<boolean> {
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.invoicesService.delete$(invoiceId).subscribe({
        next: () => {
          this.ordersRepository.notify({ code: 'INVOICE_DELETED' })
          this.setIsLoading(false)
          resolve(true)
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  async resetPicking(): Promise<boolean> {
    const order = this.order()
    if (!order) {
      this.ordersRepository.notify({ code: 'ORDER_NOT_FOUND' })
      return false
    }

    if (order.pickingListId) {
      this.ordersRepository.notify({ code: 'RESET_ORDER_PICKED' })
      return false
    }

    this.unsetError()
    const saved = await this.save(order)
    if (!saved) {
      return false
    }

    return new Promise((resolve) => {
      this.ordersService.resetPicking$(order._id).subscribe({
        next: async (data) => {
          this.setOrder(data, false)
          this.ordersRepository.notify({ code: 'RESET_ORDER_SUCCESS' })
          resolve(true)
        },
        error: (error) => {
          resolve(false)
          this.setError(error)
        },
      })
    })
  }

  async packOrder(orderId: string, printerId: string): Promise<boolean> {
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.ordersService
        .pack$(orderId, {
          printers: { labels: printerId, a4: printerId },
        })
        .subscribe({
          next: (data) => {
            this.ordersRepository.notify({ code: 'ORDER_PACKED' })
            this.setOrder(data, false)
            resolve(true)
          },
          error: (error) => {
            reject(error)
            this.setError(error)
          },
        })
    })
  }

  async printOrder(
    orderId: string,
    printerId: string,
    type: 'label' | 'summary',
  ): Promise<boolean> {
    this.unsetError()

    if (type === 'label') {
      return new Promise((resolve, reject) => {
        this.ordersService
          .printLabel$(orderId, {
            labels: printerId,
            a4: printerId,
          })
          .subscribe({
            next: async () => {
              this.ordersRepository.notify({ code: 'PRINT_SENT' })
              await this.reloadOrder()
              resolve(true)
            },
            error: (error) => {
              reject(error)
              this.setError(error)
            },
          })
      })
    } else {
      return new Promise((resolve, reject) => {
        this.ordersService.printSummary$(orderId, printerId).subscribe({
          next: async () => {
            this.ordersRepository.notify({ code: 'PRINT_SENT' })
            await this.reloadOrder()
            resolve(true)
          },
          error: (error) => {
            reject(error)
            this.setError(error)
          },
        })
      })
    }
  }

  async downloadUploadTemplate() {
    this.setIsLoading(true)
    return new Promise((resolve, reject) => {
      this.ordersService.downloadOrderRowsUploadTemplate$().subscribe({
        next: (response) => {
          resolve(response)
          const blob = response.body

          if (blob) {
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = this.translationService
              .translate('order_template')
              .replace(/"/g, '')
            document.body.appendChild(a)
            a.click()
            window.URL.revokeObjectURL(url)
            a.remove()
          }
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  async downloadAttachment(id: string) {
    const order = this.order()
    if (!order?._id) {
      return
    }

    this.setIsLoading(true)
    return new Promise((resolve, reject) => {
      this.ordersService.downloadAttachment$(order._id, id).subscribe({
        next: (response) => {
          resolve(response)
          const contentDisposition =
            response.headers.get('Content-Disposition') ||
            'attachment; filename="download"'
          const filename =
            contentDisposition.split('filename=')[1] || 'download'
          const blob = response.body

          if (blob) {
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = filename.replace(/"/g, '')
            document.body.appendChild(a)
            a.click()
            window.URL.revokeObjectURL(url)
            a.remove()
          }
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  async deleteAttachment(id: string) {
    const order = this.order()
    if (!order?._id) {
      return
    }

    const saved = await this.save(order)

    if (saved) {
      this.setIsLoading(true)
      return new Promise((resolve, reject) => {
        this.ordersService.deleteAttachment$(order._id, id).subscribe({
          next: (response) => {
            resolve(response)
            this.setOrder(response, false)
            this.ordersRepository.notify({
              code: 'ATTACHMENT_DELETED',
            })
          },
          error: (error) => {
            reject(error)
            this.setError(error)
          },
        })
      })
    } else {
      return
    }
  }

  updateAddress(updatedAddress: 'SHIPPING' | 'BILLING') {
    const order = this.order()
    // Check address recipient type
    if (order?.header.shippingAddress?.addressType !== 'CUSTOMER') {
      return
    }

    // Check addresses
    if (
      !order.header.billingAddress?.address1 &&
      !order.header.shippingAddress?.address1
    ) {
      return
    }

    const customerId =
      updatedAddress === 'SHIPPING'
        ? order.header.shippingAddress?._id
        : order.header.billingAddress?._id

    if (!customerId) {
      return
    }

    this.unsetError()
    new Promise((resolve, reject) => {
      this.customersService.read$(customerId).subscribe({
        next: (response) => {
          resolve(response)
          this.setOrder(
            updatedAddress === 'SHIPPING'
              ? setOrderBillingAddressByCustomer(order, response)
              : setOrderShippingAddressByCustomer(order, response),
            false,
          )
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  async updatePayment(paymentId: string) {
    const order = this.order()
    if (!order) {
      return
    }

    return new Promise(() => {
      this.paymentsService.read$(paymentId).subscribe({
        next: (value) => {
          if (value) {
            this.setOrder({
              ...order,
              header: {
                ...order.header,
                cashOnDelivery: checkOrderCodPayment(order, value),
              },
            })
            return
          }
          this.ordersRepository.notify({ code: 'PAYMENT_NOT_SET' })
        },
        error: (error) => {
          this.setError(error)
        },
      })
    })
  }

  setShippingAddress(shippingAddress: ShippingAddress) {
    const order = this.order()
    if (!order) {
      this.ordersRepository.notify({ code: 'ORDER_NOT_FOUND' })
      return
    }

    order.header.shippingAddress = shippingAddress
    this.setOrder(order)
  }

  setPackages(packages: OrderPackage[] | undefined) {
    const order = this.order()
    if (!order) {
      this.ordersRepository.notify({ code: 'ORDER_NOT_FOUND' })
      return
    }

    order.packages = packages
    this.setOrder(order)
  }

  setAdditionalPackages(
    additionalPackages: OrderAdditionalPackage[] | undefined,
  ) {
    const order = this.order()
    if (!order) {
      this.ordersRepository.notify({ code: 'ORDER_NOT_FOUND' })
      return
    }

    order.additionalPackages = additionalPackages
    this.setOrder(order)
  }

  setData(
    order: Order,
    isModified?: boolean,
    extData?: OrderDetailExtDataTuple,
  ) {
    const updatedOrder = updateOrderAmounts(
      initOrder(order),
      this.amountsWithTaxes(),
      this.discountPercentage(),
    )

    const cashOnDelivery = extData?.[2]
    if (cashOnDelivery) {
      updatedOrder.header.cashOnDelivery = cashOnDelivery
    }

    this.state.set({
      ...this.state(),
      order: updatedOrder,
      isModified: isModified ?? this.state().isModified,
      bordereau: extData?.[0],
      carrier: extData?.[1],
      shipment: extData?.[3]?.shipment ?? this.state()?.shipment,
      tracking: extData?.[3]?.tracking ?? this.state()?.tracking,
      pickingList: extData?.[4],
      products: extData?.[5] ?? this.products(),
      goodsReturn: extData?.[6],
      packages: extData?.[7] ?? this.packages(),
      uploadUrl: this.ordersService.getOrderAttachmentsUploadUrl(
        updatedOrder._id,
      ),
      isLoading: false,
      error: undefined,
    })
  }

  setOrder(order: Order, isModified?: boolean) {
    const updatedOrder = updateOrderAmounts(
      initOrder(order),
      this.amountsWithTaxes(),
      this.discountPercentage(),
    )

    this.state.set({
      ...this.state(),
      order: updatedOrder,
      uploadUrl: this.ordersService.getOrderAttachmentsUploadUrl(order._id),
      isModified: isModified ?? true,
      isLoading: false,
      error: undefined,
    })
  }

  setUserDefaults(tenant: Tenant, user: User) {
    this.state.set({
      ...this.state(),
      amountsWithTaxes: !!user.settings?.showPriceWithTaxes,
      discountPercentage: !!user.settings?.showDiscountPercentage,
      dropshippingEnabled: !!tenant.dropShipping?.active,
      tenantId: tenant._id,
    })
  }

  setOrderData(order?: Order, defaults?: DeepPartial<Order>) {
    let updatedOrder = updateOrderAmounts(
      initOrder(order),
      this.amountsWithTaxes(),
      this.discountPercentage(),
    )

    if (defaults) {
      updatedOrder = setOrderDefaults(updatedOrder, defaults)
    }

    this.state.set({
      ...this.state(),
      order: updatedOrder,
      uploadUrl: this.ordersService.getOrderAttachmentsUploadUrl(
        updatedOrder._id,
      ),
      isLoading: false,
    })
  }

  updateAmounts() {
    setTimeout(() => {
      const order = this.order()
      if (!order) {
        return
      }

      this.state.set({
        ...this.state(),
        order: updateOrderAmounts(
          order,
          this.amountsWithTaxes(),
          this.discountPercentage(),
        ),
        isLoading: false,
      })
    }, 1)
  }

  setOrderProducts(order?: Order, products?: Product[]) {
    this.state.set({
      ...this.state(),
      order,
      products: products ?? [],
      isLoading: false,
    })
  }

  setShipmentData(shipmentData: OrderShipmentData) {
    this.state.set({
      ...this.state(),
      shipment: shipmentData.shipment,
      tracking: shipmentData.tracking,
    })
  }

  addProducts(selection: ProductSelection[]) {
    const order = this.order()
    if (!order) {
      this.ordersRepository.notify({ code: 'ORDER_NOT_FOUND' })
      return
    }

    this.state.set({
      ...this.state(),
      order: addOrderProducts(order, selection, this.amountsWithTaxes()),
      products: uniqBy(
        [...this.products(), ...selection.map((s) => s.product)],
        '_id',
      ),
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }

  detailDataToTuple(
    data?: OrderDetailData,
  ): OrderDetailExtDataTuple | undefined {
    return data
      ? undefined
      : [
          data!.bordereau,
          data!.carrier,
          data!.cod,
          data!.shipmentData,
          data!.pickingList,
          data!.products,
          data!.goodsReturn,
          data!.packages,
        ]
  }

  setAmountEditable(amountsEditable: boolean) {
    this.state.set({
      ...this.state(),
      amountsEditable,
    })
  }

  setDiscountPercentage(discountPercentage: boolean) {
    const order = this.order()

    this.state.set({
      ...this.state(),
      discountPercentage,
      order: order
        ? updateOrderAmounts(order, this.amountsWithTaxes(), discountPercentage)
        : this.state().order,
    })
  }

  setAmountsWithTaxes(amountsWithTaxes: boolean) {
    const order = this.order()

    this.state.set({
      ...this.state(),
      amountsWithTaxes,
      order: order
        ? updateOrderAmounts(order, amountsWithTaxes, this.discountPercentage())
        : this.state().order,
    })
  }

  async setExtDataPackages(order: Order) {
    const packages = await this.loadPackages(order)
    this.state.set({
      ...this.state(),
      packages: packages ?? this.packages(),
    })
  }
}
